import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtPrice } from "../components/VehicleCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Upload, X, Car, ImageIcon } from "lucide-react";

const EMPTY = {
  brand: "", model: "", year: "", month: "none", price: "", promo_price: "", mileage: "", fuel: "Gasolina", gearbox: "Manual",
  color: "", segment: "", horsepower: "", displacement: "", doors: "", extras: "", description: "", images: [], sold: false, featured: false,
};

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!checking && !user) navigate("/login");
  }, [checking, user, navigate]);

  const load = useCallback(async () => {
    const { data } = await api.get("/vehicles");
    setVehicles(data);
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v, month: v.month ? String(v.month) : "none", segment: v.segment ?? "", promo_price: v.promo_price ?? "", horsepower: v.horsepower ?? "", displacement: v.displacement ?? "", doors: v.doors ?? "" });
    setEditId(v.id);
    setOpen(true);
  };

  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      set("images", [...form.images, ...paths]);
      toast.success(`${paths.length} foto(s) carregada(s)`);
    } catch (err) {
      toast.error("Erro ao carregar fotos");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => set("images", form.images.filter((_, i) => i !== idx));

  const save = async () => {
    if (!form.brand || !form.model || !form.year || !form.price) {
      toast.error("Preencha marca, modelo, ano e preço");
      return;
    }
    setSaving(true);
    const payload = {
      brand: form.brand, model: form.model, year: parseInt(form.year), month: form.month && form.month !== "none" ? parseInt(form.month) : null, price: parseFloat(form.price),
      promo_price: form.promo_price ? parseFloat(form.promo_price) : null,
      mileage: parseInt(form.mileage) || 0, fuel: form.fuel, gearbox: form.gearbox, color: form.color, segment: form.segment,
      horsepower: form.horsepower ? parseInt(form.horsepower) : null,
      displacement: form.displacement ? parseInt(form.displacement) : null,
      doors: form.doors ? parseInt(form.doors) : null,
      extras: form.extras, description: form.description, images: form.images, sold: form.sold, featured: form.featured,
    };
    try {
      if (editId) await api.put(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editId ? "Viatura atualizada" : "Viatura adicionada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/vehicles/${deleteId}`);
      toast.success("Viatura removida");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error("Erro ao remover");
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (checking || !user) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="glass border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary text-primary-foreground font-heading font-black">LF</span>
            <span className="font-heading font-extrabold text-lg tracking-[0.1em]">PAINEL · STAND LF</span>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-analytics" variant="ghost" onClick={() => navigate("/admin/analytics")} className="rounded-full font-semibold hidden sm:flex">Estatísticas</Button>
            <Button data-testid="admin-blog" variant="ghost" onClick={() => navigate("/admin/blog")} className="rounded-full font-semibold hidden sm:flex">Gerir Blog</Button>
            <Button data-testid="admin-view-site" variant="ghost" onClick={() => navigate("/")} className="rounded-full font-semibold hidden sm:flex">Ver site</Button>
            <Button data-testid="admin-logout" variant="outline" onClick={doLogout} className="rounded-full font-semibold border-2"><LogOut className="w-4 h-4 mr-1.5" /> Sair</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">Viaturas</h1>
            <p className="text-muted-foreground text-sm mt-1">{vehicles.length} viatura(s) no catálogo</p>
          </div>
          <Button data-testid="admin-add-vehicle" onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 px-5 transition-transform hover:-translate-y-0.5">
            <Plus className="w-4 h-4 mr-1.5" /> Adicionar viatura
          </Button>
        </div>

        {vehicles.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl border border-black/5">
            <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="font-heading font-bold text-xl">Ainda sem viaturas</p>
            <p className="text-muted-foreground mt-1">Adicione a primeira viatura ao catálogo.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {vehicles.map((v) => (
              <div key={v.id} data-testid="admin-vehicle-item" className="bg-white rounded-2xl overflow-hidden border border-black/5">
                <div className="aspect-video bg-muted relative">
                  {v.images?.length ? <img src={fileUrl(v.images[0])} alt={v.model} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-muted-foreground" /></div>}
                  {v.sold && <span className="absolute top-3 left-3 bg-zinc-900 text-white text-xs font-bold px-2.5 py-1 rounded-full">Vendido</span>}
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-lg">{v.brand} {v.model}</h3>
                  <p className="text-primary font-bold mt-0.5">{fmtPrice(v.price)}</p>
                  <p className="text-muted-foreground text-sm mt-1">{v.month ? `${String(v.month).padStart(2, "0")}/${v.year}` : v.year} · {new Intl.NumberFormat("pt-PT").format(v.mileage)} km · {v.fuel}</p>
                  <div className="flex gap-2 mt-4">
                    <Button data-testid="admin-edit-btn" variant="outline" onClick={() => openEdit(v)} className="flex-1 rounded-full font-semibold border-2 h-9"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar</Button>
                    <Button data-testid="admin-delete-btn" variant="ghost" onClick={() => setDeleteId(v.id)} className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10 h-9 px-3"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* FORM DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="vehicle-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editId ? "Editar viatura" : "Nova viatura"}</DialogTitle>
            <DialogDescription>Preencha os dados da viatura e carregue as fotos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* IMAGES */}
            <div>
              <Label className="text-sm font-semibold">Fotos</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {form.images.map((img, i) => (
                  <div key={img} className="relative w-24 h-20 rounded-xl overflow-hidden border">
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button data-testid={`remove-image-${i}`} onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <button data-testid="upload-image-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-24 h-20 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                  {uploading ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /> : <><Upload className="w-5 h-5" /><span className="text-xs mt-1">Carregar</span></>}
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleUpload} data-testid="file-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Marca *" v={form.brand} on={(x) => set("brand", x)} tid="form-brand" placeholder="BMW" />
              <Field label="Modelo *" v={form.model} on={(x) => set("model", x)} tid="form-model" placeholder="Série 3" />
              <div>
                <Label className="text-sm font-semibold">Mês</Label>
                <Select value={form.month} onValueChange={(x) => set("month", x)}>
                  <SelectTrigger data-testid="form-month" className="mt-1.5 h-10 rounded-xl"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"].map((m, i) => <SelectItem key={m} value={String(i + 1)}>{String(i + 1).padStart(2, "0")} — {m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Ano *" type="number" v={form.year} on={(x) => set("year", x)} tid="form-year" placeholder="2020" />
              <Field label="Preço (€) *" type="number" v={form.price} on={(x) => set("price", x)} tid="form-price" placeholder="25000" />
              <Field label="Preço promocional (€)" type="number" v={form.promo_price} on={(x) => set("promo_price", x)} tid="form-promo-price" placeholder="Opcional — preço novo" />
              <Field label="Quilómetros" type="number" v={form.mileage} on={(x) => set("mileage", x)} tid="form-mileage" placeholder="80000" />
              <div>
                <Label className="text-sm font-semibold">Combustível</Label>
                <Select value={form.fuel} onValueChange={(x) => set("fuel", x)}>
                  <SelectTrigger data-testid="form-fuel" className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Gasolina", "Diesel", "Híbrido", "Elétrico", "GPL"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Caixa</Label>
                <Select value={form.gearbox} onValueChange={(x) => set("gearbox", x)}>
                  <SelectTrigger data-testid="form-gearbox" className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Manual", "Automática"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">Segmento</Label>
                <Select value={form.segment || undefined} onValueChange={(x) => set("segment", x)}>
                  <SelectTrigger data-testid="form-segment" className="mt-1.5 h-10 rounded-xl"><SelectValue placeholder="Escolher" /></SelectTrigger>
                  <SelectContent>{["Citadino", "Utilitário", "Familiar", "SUV", "Carrinha", "Monovolume", "Comercial", "Coupé"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Cor" v={form.color} on={(x) => set("color", x)} tid="form-color" placeholder="Preto" />
              <Field label="Potência (cv)" type="number" v={form.horsepower} on={(x) => set("horsepower", x)} tid="form-horsepower" placeholder="150" />
              <Field label="Cilindrada (cc)" type="number" v={form.displacement} on={(x) => set("displacement", x)} tid="form-displacement" placeholder="1995" />
              <Field label="Portas" type="number" v={form.doors} on={(x) => set("doors", x)} tid="form-doors" placeholder="5" />
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="form-sold" checked={form.sold} onChange={(e) => set("sold", e.target.checked)} className="w-4 h-4 accent-primary" />
                  <span className="text-sm font-semibold">Marcar como vendido</span>
                </label>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="form-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-primary" />
                  <span className="text-sm font-semibold">Destacar na página inicial</span>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Extras</Label>
              <Textarea data-testid="form-extras" value={form.extras} onChange={(e) => set("extras", e.target.value)} placeholder="Ex: Estofos em pele, GPS, Câmara traseira..." className="mt-1.5 rounded-xl min-h-[80px]" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Informações / Descrição</Label>
              <Textarea data-testid="form-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Escreva aqui informações adicionais sobre a viatura..." className="mt-1.5 rounded-xl min-h-[100px]" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-full font-semibold border-2 h-11">Cancelar</Button>
              <Button data-testid="form-save" onClick={save} disabled={saving} className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11">{saving ? "A guardar..." : "Guardar"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remover viatura?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação é permanente e não pode ser revertida.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={confirmDelete} className="rounded-full bg-destructive hover:bg-destructive/90">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, v, on, tid, type = "text", placeholder }) => (
  <div>
    <Label className="text-sm font-semibold">{label}</Label>
    <Input data-testid={tid} type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="mt-1.5 h-10 rounded-xl focus-visible:ring-primary" />
  </div>
);
